import React, { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import scheduleHistoryQuery from '../../../utils/apollo/scheduler/scheduleHistoryQuery';
import {
  clearHistoryAction,
  loadCurrentHistoryAction,
} from '../../../redux/actions/scheduler';
import styled from 'styled-components';
import { colors, getScrollBarStyles } from '../../../config/styles';
import { ScheduleRunOutcome } from '../../../utils/apollo/graphqlSchema';
import { formatNumberWithComma } from '../../../utils/formatText';
import Badge from '../../../components/Badge';
import { globalFormat } from '../../../utils/formatting';
import { LoadingSpinner } from '../../../components/Loaders';
import useBrowserTimezoneLabel from '../../../utils/hooks/useBrowserTimezoneLabel';
import { theme } from '../../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

interface IProps {
  scheduleId: string;
}

const Container = styled.div`
  max-height: 300px;
  min-height: 80px;
  width: 400px;
  color: ${theme.colors.secondary800};
  overflow-y: auto;
  ${getScrollBarStyles(4)};
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const StyledTableHeading = styled.th`
  width: 200px;
  padding: 12px 8px;
  border-bottom: 1px solid ${theme.colors.border};
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const StyledCell = styled.td`
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  color: ${theme.colors.secondary800};
`;
const FailedOutcome = styled.div`
  color: ${colors.mainError};
`;

const EmptyRecord = styled.div`
  font-size: 12px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.secondary800};
`;
const TimeZoneBadge = styled(Badge)`
  margin-left: 4px;
`;

const ScheduleCohortImportedHistory = ({ scheduleId }: IProps) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const dispatch = useAppDispatch();
  const browserTimeZoneLabel = useBrowserTimezoneLabel();
  const onScheduleHistoryFetch = useCallback(
    ({ scheduleHistory }: any) => {
      dispatch(loadCurrentHistoryAction(scheduleHistory));
    },
    [dispatch],
  );

  const { loading } = useQuery(scheduleHistoryQuery, {
    variables: {
      scheduleId,
    },
    onCompleted: onScheduleHistoryFetch,
  });
  const scheduleHistory = useAppSelector(
    (state) => state?.scheduler?.currentHistory,
  );

  useEffect(() => {
    return () => {
      dispatch(clearHistoryAction());
    };
  }, [dispatch]);

  if (loading) {
    return (
      <Container>
        <LoadingSpinner active />
      </Container>
    );
  }

  if (!scheduleHistory?.length) {
    return (
      <Container>
        <EmptyRecord>No Record Found</EmptyRecord>
      </Container>
    );
  }

  return (
    <Container>
      <StyledTable>
        <thead>
          <tr>
            <StyledTableHeading>
              Import Time
              <TimeZoneBadge color="grey" size="primary">
                {browserTimeZoneLabel}
              </TimeZoneBadge>
            </StyledTableHeading>
            <StyledTableHeading>Users Imported</StyledTableHeading>
          </tr>
        </thead>
        <tbody>
          {scheduleHistory.map((historyRecord: any) => {
            const {
              schedule: { id: currentRecordScheduleId },
              runAt,
              importedUsers,
              outcome,
            } = historyRecord;
            return (
              <tr key={currentRecordScheduleId}>
                <StyledCell>
                  {globalFormat.formatDate(runAt, { dateFormat, timeFormat })}
                </StyledCell>
                <StyledCell>
                  {outcome === ScheduleRunOutcome.Failed ? (
                    <FailedOutcome>{outcome.toLowerCase()}</FailedOutcome>
                  ) : (
                    formatNumberWithComma(importedUsers)
                  )}
                </StyledCell>
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
    </Container>
  );
};

export default ScheduleCohortImportedHistory;
